import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { Youtube, Instagram, Rss, Facebook, ExternalLink, Clock } from 'lucide-react';

const PLATFORM = {
  youtube: { icon: <Youtube size={14} className="text-red-500" />, label: 'YouTube', color: 'border-red-500/25 bg-red-500/8' },
  instagram: { icon: <Instagram size={14} className="text-pink-400" />, label: 'Instagram', color: 'border-pink-400/25 bg-pink-400/8' },
  facebook: { icon: <Facebook size={14} className="text-blue-400" />, label: 'Facebook', color: 'border-blue-400/25 bg-blue-400/8' },
  rss: { icon: <Rss size={14} className="text-bx-gold" />, label: 'Blog', color: 'border-bx-gold/25 bg-bx-gold/8' },
};

const formatDate = (str) => {
  if (!str) return null;
  try { return new Date(str).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch { return null; }
};

function FeedCard({ item }) {
  const p = PLATFORM[item.type] || PLATFORM.rss;
  const hasContent = item.title || item.description;

  return (
    <motion.a
      href={item.latest_url || item.url}
      target="_blank" rel="noreferrer noopener"
      whileHover={{ y: -2 }}
      className={`block rounded-xl border p-3 transition hover:brightness-110 ${p.color}`}
      data-testid={`creator-card-${item.type}`}
    >
      {/* YouTube thumbnail */}
      {item.type === 'youtube' && item.thumb && (
        <div className="relative rounded-lg overflow-hidden mb-2.5 aspect-video bg-black/40">
          <img src={item.thumb} alt={item.title || ''} className="w-full h-full object-cover" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="h-9 w-9 rounded-full bg-red-600/90 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="white" className="w-4 h-4 ml-0.5"><path d="M8 5v14l11-7z"/></svg>
            </span>
          </span>
        </div>
      )}

      <div className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5">{p.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-white/50 mb-0.5">{item.label || p.label}</p>
          {hasContent ? (
            <>
              <p className="text-sm text-white/90 font-medium leading-snug line-clamp-2">{item.title}</p>
              {item.description && !item.title && (
                <p className="text-xs text-white/60 mt-0.5 line-clamp-2">{item.description}</p>
              )}
              {formatDate(item.published) && (
                <p className="flex items-center gap-1 text-[10px] text-white/40 mt-1">
                  <Clock size={9} /> {formatDate(item.published)}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-white/70">Visit {p.label} profile</p>
          )}
        </div>
        <ExternalLink size={11} className="text-white/30 shrink-0 mt-0.5" />
      </div>
    </motion.a>
  );
}

export default function CreatorFeed({ bubbleId }) {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/bubbles/${bubbleId}/feed`)
      .then(({ data }) => setFeed(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [bubbleId]);

  if (loading) return null;
  if (!feed.length) return null;

  return (
    <div data-testid="creator-feed">
      <h3 className="text-xs uppercase tracking-widest text-white/50 mb-3">Creator World</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {feed.filter((f) => !f.error).map((item, i) => (
          <FeedCard key={i} item={item} />
        ))}
      </div>
    </div>
  );
}
